import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Clock3, Edit, Plus, RefreshCw, SearchCheck } from 'lucide-react';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SearchBar from '@/components/shared/SearchBar';

const EMPTY_RULE_FORM = {
  origin_country: '',
  origin_city: '',
  destination_country: '',
  destination_city: '',
  service_tier: 'standard',
  min_hours: '24',
  max_hours: '48',
  business_days_only: false,
  is_active: true,
  priority: '100',
  notes: '',
};

const EMPTY_PREVIEW_FORM = {
  origin_country: '',
  origin_city: '',
  destination_country: '',
  destination_city: '',
  service_tier: 'standard',
};

const DEFAULT_PAYLOAD = {
  items: [],
  summary: { total: 0, active: 0, inactive: 0 },
};

const normalizeCountries = (countries = []) =>
  (countries || []).map((country) => ({
    id: String(country?.code || '').trim().toUpperCase(),
    label: String(country?.name || country?.code || '').trim(),
    cities: (country?.cities || []).map((city) => ({
      id: String(city?.code || '').trim().toUpperCase(),
      label: String(city?.name || city?.code || '').trim(),
    })),
  }));

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR');
};

const getServiceLabel = (value) => (value === 'express' ? 'Express' : 'Standard');

const formatLocation = (cityCode, cityName, countryCode, countryName) => {
  if (cityName || cityCode) {
    return `${cityName || cityCode}${countryName || countryCode ? `, ${countryName || countryCode}` : ''}`;
  }
  if (countryName || countryCode) {
    return `${countryName || countryCode} (toutes villes)`;
  }
  return '-';
};

const formatRouteLabel = (rule) => {
  const origin = formatLocation(
    rule.origin_city,
    rule.origin_city_name,
    rule.origin_country,
    rule.origin_country_name,
  );
  const destination = formatLocation(
    rule.destination_city,
    rule.destination_city_name,
    rule.destination_country,
    rule.destination_country_name,
  );
  return `${origin} -> ${destination}`;
};

const toRulePayload = (formData) => ({
  origin_country: formData.origin_country || undefined,
  origin_city: formData.origin_city || undefined,
  destination_country: formData.destination_country || undefined,
  destination_city: formData.destination_city || undefined,
  service_tier: formData.service_tier,
  min_hours: Number(formData.min_hours),
  max_hours: Number(formData.max_hours),
  business_days_only: Boolean(formData.business_days_only),
  is_active: Boolean(formData.is_active),
  priority: Number(formData.priority || 0),
  notes: String(formData.notes || '').trim() || undefined,
});

const toPreviewPayload = (formData) => ({
  origin_country: formData.origin_country || undefined,
  origin_city: formData.origin_city || undefined,
  destination_country: formData.destination_country || undefined,
  destination_city: formData.destination_city || undefined,
  service_tier: formData.service_tier,
});

const mapRuleToForm = (rule) => ({
  origin_country: rule.origin_country || '',
  origin_city: rule.origin_city || '',
  destination_country: rule.destination_country || '',
  destination_city: rule.destination_city || '',
  service_tier: rule.service_tier || 'standard',
  min_hours: String(rule.min_hours ?? 0),
  max_hours: String(rule.max_hours ?? 0),
  business_days_only: Boolean(rule.business_days_only),
  is_active: Boolean(rule.is_active),
  priority: String(rule.priority ?? 100),
  notes: rule.notes || '',
});

const getSlaApiErrorMessage = (detail) => {
  const normalized = String(detail || '').trim();
  if (normalized === 'INVALID_SLA_HOURS') return 'Le delai min/max est invalide.';
  if (normalized === 'INVALID_SLA_PRIORITY') return 'La priorite est invalide.';
  if (normalized === 'ORIGIN_SCOPE_REQUIRED') return 'Renseignez au moins un scope d origine.';
  if (normalized === 'DESTINATION_SCOPE_REQUIRED') return 'Renseignez au moins un scope de destination.';
  if (normalized === 'SLA_RULE_NOT_FOUND') return 'La regle SLA est introuvable.';
  if (normalized === 'NO_FIELDS_TO_UPDATE') return 'Aucune modification n a ete detectee.';
  return normalized || 'Une erreur est survenue.';
};

const DeliverySlaManagement = () => {
  const [slaPayload, setSlaPayload] = useState(DEFAULT_PAYLOAD);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState(EMPTY_RULE_FORM);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ service: 'all', status: 'all' });
  const [previewForm, setPreviewForm] = useState(EMPTY_PREVIEW_FORM);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);

  const normalizedCountries = useMemo(() => normalizeCountries(countries), [countries]);

  const availableOriginCities = useMemo(() => {
    const selectedCountry = normalizedCountries.find((country) => country.id === formData.origin_country);
    return selectedCountry?.cities || [];
  }, [formData.origin_country, normalizedCountries]);

  const availableDestinationCities = useMemo(() => {
    const selectedCountry = normalizedCountries.find((country) => country.id === formData.destination_country);
    return selectedCountry?.cities || [];
  }, [formData.destination_country, normalizedCountries]);

  const previewOriginCities = useMemo(() => {
    const selectedCountry = normalizedCountries.find((country) => country.id === previewForm.origin_country);
    return selectedCountry?.cities || [];
  }, [normalizedCountries, previewForm.origin_country]);

  const previewDestinationCities = useMemo(() => {
    const selectedCountry = normalizedCountries.find((country) => country.id === previewForm.destination_country);
    return selectedCountry?.cities || [];
  }, [normalizedCountries, previewForm.destination_country]);

  const filteredItems = useMemo(() => {
    const query = String(searchQuery || '').trim().toLowerCase();
    return (slaPayload.items || []).filter((rule) => {
      if (!query) return true;
      const haystack = [
        rule.origin_city_name,
        rule.origin_city,
        rule.origin_country_name,
        rule.origin_country,
        rule.destination_city_name,
        rule.destination_city,
        rule.destination_country_name,
        rule.destination_country,
        rule.service_tier,
        rule.label,
        rule.notes,
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [searchQuery, slaPayload.items]);

  const summary = useMemo(() => {
    const items = slaPayload.items || [];
    return {
      total: slaPayload.summary?.total ?? items.length,
      active: slaPayload.summary?.active ?? items.filter((item) => item.is_active).length,
      inactive: slaPayload.summary?.inactive ?? items.filter((item) => !item.is_active).length,
      express: items.filter((item) => item.service_tier === 'express').length,
    };
  }, [slaPayload]);

  const loadCountries = async () => {
    const response = await axios.get(`${API}/countries`);
    setCountries(response.data || []);
  };

  const loadRules = async (nextFilters = filters) => {
    const params = {};
    if (nextFilters.service !== 'all') params.service_tier = nextFilters.service;
    if (nextFilters.status === 'active') params.is_active = true;
    if (nextFilters.status === 'inactive') params.is_active = false;
    const response = await axios.get(`${API}/admin/delivery-sla-rules`, { params });
    setSlaPayload(response.data || DEFAULT_PAYLOAD);
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadCountries(), loadRules(filters)]);
    } catch (error) {
      toast.error(getSlaApiErrorMessage(error.response?.data?.detail || 'Impossible de charger les regles SLA.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (loading) return;
    loadRules(filters).catch((error) => {
      toast.error(getSlaApiErrorMessage(error.response?.data?.detail || 'Impossible de filtrer les regles SLA.'));
    });
  }, [filters.service, filters.status]);

  const openCreateDialog = () => {
    setEditingRule(null);
    setFormData(EMPTY_RULE_FORM);
    setShowDialog(true);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData(mapRuleToForm(rule));
    setShowDialog(true);
  };

  const handleRefresh = async () => {
    try {
      await loadRules(filters);
    } catch (error) {
      toast.error(getSlaApiErrorMessage(error.response?.data?.detail || 'Impossible d actualiser les regles SLA.'));
    }
  };

  const validateForm = () => {
    const minHours = Number(formData.min_hours);
    const maxHours = Number(formData.max_hours);
    const priority = Number(formData.priority);

    if (!formData.origin_country && !formData.origin_city) return 'Choisissez au moins un scope d origine.';
    if (!formData.destination_country && !formData.destination_city) return 'Choisissez au moins un scope de destination.';
    if (!Number.isFinite(minHours) || !Number.isFinite(maxHours)) return 'Les delais min et max sont obligatoires.';
    if (minHours < 0 || maxHours <= 0 || minHours > maxHours) return 'Le delai min/max est invalide.';
    if (!Number.isFinite(priority)) return 'La priorite est obligatoire.';
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = toRulePayload(formData);
      if (editingRule) {
        await axios.patch(`${API}/admin/delivery-sla-rules/${editingRule.id}`, payload);
        toast.success('Regle SLA mise a jour.');
      } else {
        await axios.post(`${API}/admin/delivery-sla-rules`, payload);
        toast.success('Regle SLA creee.');
      }
      setShowDialog(false);
      setEditingRule(null);
      setFormData(EMPTY_RULE_FORM);
      await loadRules(filters);
    } catch (error) {
      toast.error(getSlaApiErrorMessage(error.response?.data?.detail || 'Impossible d enregistrer cette regle SLA.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (event) => {
    event.preventDefault();
    if (!previewForm.origin_country && !previewForm.origin_city) {
      toast.error('Selectionnez au moins un scope d origine pour le test.');
      return;
    }
    if (!previewForm.destination_country && !previewForm.destination_city) {
      toast.error('Selectionnez au moins un scope de destination pour le test.');
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await axios.post(`${API}/admin/delivery-sla-rules/resolve`, toPreviewPayload(previewForm));
      setPreviewResult(response.data?.resolution || null);
    } catch (error) {
      toast.error(getSlaApiErrorMessage(error.response?.data?.detail || 'Impossible de tester cette regle.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Clock3 className="h-6 w-6 text-cyan-600" />
                Delais de livraison
              </CardTitle>
              <CardDescription>
                Pilotez les delais par trajet, niveau de service et priorite. Le backend reste l unique moteur de resolution.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleRefresh}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Actualiser
              </Button>
              <Button data-testid="create-sla-rule-btn" className="bg-cyan-500 hover:bg-cyan-600" onClick={openCreateDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Nouvelle regle
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardDescription>Total</CardDescription>
                <CardTitle>{summary.total}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-green-100 bg-green-50">
              <CardHeader className="pb-2">
                <CardDescription>Actives</CardDescription>
                <CardTitle className="text-green-700">{summary.active}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-gray-200">
              <CardHeader className="pb-2">
                <CardDescription>Inactives</CardDescription>
                <CardTitle>{summary.inactive}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-cyan-100 bg-cyan-50">
              <CardHeader className="pb-2">
                <CardDescription>Express</CardDescription>
                <CardTitle className="text-cyan-700">{summary.express}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.6fr,1fr,1fr]">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Rechercher un trajet, une ville ou une note..."
            />
            <select
              data-testid="sla-filter-service"
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.service}
              onChange={(event) => setFilters((current) => ({ ...current, service: event.target.value }))}
            >
              <option value="all">Tous les services</option>
              <option value="standard">Standard</option>
              <option value="express">Express</option>
            </select>
            <select
              data-testid="sla-filter-status"
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.status}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actives</option>
              <option value="inactive">Inactives</option>
            </select>
          </div>

          {filteredItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 px-6 py-12 text-center text-gray-500">
              Aucune regle SLA ne correspond aux filtres.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trajet</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Delai estime</TableHead>
                  <TableHead>Priorite</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Mise a jour</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{formatRouteLabel(rule)}</div>
                        {rule.notes && <div className="max-w-xs text-xs text-gray-500">{rule.notes}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getServiceLabel(rule.service_tier)}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{rule.label || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {rule.business_days_only ? 'Jours ouvrables uniquement' : 'Jours calendaires'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{rule.priority}</TableCell>
                    <TableCell>
                      {rule.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-200 text-gray-700">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{formatDateTime(rule.updated_at)}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid={`edit-sla-rule-${rule.id}`}
                        onClick={() => openEditDialog(rule)}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Modifier
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SearchCheck className="h-5 w-5 text-cyan-600" />
            Tester la regle
          </CardTitle>
          <CardDescription>
            Lancez un preview de resolution sur le backend pour verifier le delai retenu pour un trajet donne.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handlePreview} className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-4 rounded-xl border border-gray-200 p-4">
              <div className="space-y-1">
                <div className="text-sm font-medium text-gray-900">Origine</div>
                <div className="text-xs text-gray-500">Selectionnez le pays puis la ville si la regle est specifique.</div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-preview-origin-country">Pays origine</Label>
                <select
                  id="sla-preview-origin-country"
                  data-testid="sla-preview-origin-country"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={previewForm.origin_country}
                  onChange={(event) => setPreviewForm((current) => ({ ...current, origin_country: event.target.value, origin_city: '' }))}
                >
                  <option value="">Selectionner un pays</option>
                  {normalizedCountries.map((country) => (
                    <option key={country.id} value={country.id}>{country.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-preview-origin-city">Ville origine</Label>
                <select
                  id="sla-preview-origin-city"
                  data-testid="sla-preview-origin-city"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={previewForm.origin_city}
                  onChange={(event) => setPreviewForm((current) => ({ ...current, origin_city: event.target.value }))}
                >
                  <option value="">Toutes les villes du pays</option>
                  {previewOriginCities.map((city) => (
                    <option key={city.id} value={city.id}>{city.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4 rounded-xl border border-gray-200 p-4">
              <div className="space-y-1">
                <div className="text-sm font-medium text-gray-900">Destination</div>
                <div className="text-xs text-gray-500">Le preview utilise la meme logique de matching que la creation livraison.</div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-preview-destination-country">Pays destination</Label>
                <select
                  id="sla-preview-destination-country"
                  data-testid="sla-preview-destination-country"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={previewForm.destination_country}
                  onChange={(event) => setPreviewForm((current) => ({ ...current, destination_country: event.target.value, destination_city: '' }))}
                >
                  <option value="">Selectionner un pays</option>
                  {normalizedCountries.map((country) => (
                    <option key={country.id} value={country.id}>{country.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-preview-destination-city">Ville destination</Label>
                <select
                  id="sla-preview-destination-city"
                  data-testid="sla-preview-destination-city"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={previewForm.destination_city}
                  onChange={(event) => setPreviewForm((current) => ({ ...current, destination_city: event.target.value }))}
                >
                  <option value="">Toutes les villes du pays</option>
                  {previewDestinationCities.map((city) => (
                    <option key={city.id} value={city.id}>{city.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4 rounded-xl border border-cyan-100 bg-cyan-50 p-4">
              <div className="space-y-2">
                <Label htmlFor="sla-preview-service">Service</Label>
                <select
                  id="sla-preview-service"
                  data-testid="sla-preview-service"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={previewForm.service_tier}
                  onChange={(event) => setPreviewForm((current) => ({ ...current, service_tier: event.target.value }))}
                >
                  <option value="standard">Standard</option>
                  <option value="express">Express</option>
                </select>
              </div>
              <div className="rounded-lg border border-cyan-200 bg-white p-3 text-sm text-gray-600">
                Le frontend ne recalcule rien. Le backend retourne la regle trouvee, le label SLA et l ETA.
              </div>
              <Button type="submit" data-testid="submit-sla-preview" className="w-full bg-cyan-500 hover:bg-cyan-600" disabled={previewLoading}>
                {previewLoading ? 'Test en cours...' : 'Tester la regle'}
              </Button>
            </div>
          </form>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            {!previewResult ? (
              <div className="text-sm text-gray-500">Aucun preview lance pour le moment.</div>
            ) : previewResult.resolved ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Trajet</div>
                  <div className="mt-1 font-medium">
                    {formatRouteLabel(previewResult.rule || previewResult.snapshot || {})}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Delai estime</div>
                  <div className="mt-1 font-medium">{previewResult.label || '-'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">ETA</div>
                  <div className="mt-1 font-medium">{formatDateTime(previewResult.estimated_delivery_date)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Priorite</div>
                  <div className="mt-1 font-medium">{previewResult.priority ?? '-'}</div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="font-medium text-gray-900">Aucune regle active n a ete trouvee.</div>
                <div className="text-sm text-gray-500">
                  Le backend a retourne le motif: {previewResult.reason || 'non precise'}.
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Modifier la regle SLA' : 'Nouvelle regle SLA'}</DialogTitle>
            <DialogDescription>
              Configurez un delai par origine, destination et niveau de service. Le backend reste le validateur final.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-4 rounded-xl border border-gray-200 p-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900">Origine</div>
                  <div className="text-xs text-gray-500">Laissez la ville vide si vous voulez une regle plus generique a l echelle du pays.</div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sla-origin-country">Pays origine *</Label>
                  <select
                    id="sla-origin-country"
                    data-testid="sla-origin-country"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.origin_country}
                    onChange={(event) => setFormData((current) => ({ ...current, origin_country: event.target.value, origin_city: '' }))}
                    required={!formData.origin_city}
                  >
                    <option value="">Selectionner un pays</option>
                    {normalizedCountries.map((country) => (
                      <option key={country.id} value={country.id}>{country.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sla-origin-city">Ville origine</Label>
                  <select
                    id="sla-origin-city"
                    data-testid="sla-origin-city"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.origin_city}
                    onChange={(event) => setFormData((current) => ({ ...current, origin_city: event.target.value }))}
                  >
                    <option value="">Toutes les villes du pays</option>
                    {availableOriginCities.map((city) => (
                      <option key={city.id} value={city.id}>{city.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4 rounded-xl border border-gray-200 p-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium text-gray-900">Destination</div>
                  <div className="text-xs text-gray-500">La regle la plus prioritaire et la plus specifique sera retenue au moment de la creation livraison.</div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sla-destination-country">Pays destination *</Label>
                  <select
                    id="sla-destination-country"
                    data-testid="sla-destination-country"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.destination_country}
                    onChange={(event) => setFormData((current) => ({ ...current, destination_country: event.target.value, destination_city: '' }))}
                    required={!formData.destination_city}
                  >
                    <option value="">Selectionner un pays</option>
                    {normalizedCountries.map((country) => (
                      <option key={country.id} value={country.id}>{country.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sla-destination-city">Ville destination</Label>
                  <select
                    id="sla-destination-city"
                    data-testid="sla-destination-city"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.destination_city}
                    onChange={(event) => setFormData((current) => ({ ...current, destination_city: event.target.value }))}
                  >
                    <option value="">Toutes les villes du pays</option>
                    {availableDestinationCities.map((city) => (
                      <option key={city.id} value={city.id}>{city.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="sla-service-tier">Service *</Label>
                <select
                  id="sla-service-tier"
                  data-testid="sla-service-tier"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.service_tier}
                  onChange={(event) => setFormData((current) => ({ ...current, service_tier: event.target.value }))}
                >
                  <option value="standard">Standard</option>
                  <option value="express">Express</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-min-hours">Delai min (heures) *</Label>
                <Input
                  id="sla-min-hours"
                  data-testid="sla-min-hours"
                  type="number"
                  min="0"
                  value={formData.min_hours}
                  onChange={(event) => setFormData((current) => ({ ...current, min_hours: event.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-max-hours">Delai max (heures) *</Label>
                <Input
                  id="sla-max-hours"
                  data-testid="sla-max-hours"
                  type="number"
                  min="1"
                  value={formData.max_hours}
                  onChange={(event) => setFormData((current) => ({ ...current, max_hours: event.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sla-priority">Priorite *</Label>
                <Input
                  id="sla-priority"
                  data-testid="sla-priority"
                  type="number"
                  min="0"
                  value={formData.priority}
                  onChange={(event) => setFormData((current) => ({ ...current, priority: event.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sla-notes">Notes</Label>
              <Textarea
                id="sla-notes"
                data-testid="sla-notes"
                rows={4}
                value={formData.notes}
                onChange={(event) => setFormData((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Exemple: livraison intraville prioritaire pour les commandes prepayees."
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
                <input
                  data-testid="sla-business-days-only"
                  type="checkbox"
                  checked={formData.business_days_only}
                  onChange={(event) => setFormData((current) => ({ ...current, business_days_only: event.target.checked }))}
                />
                <span className="text-sm text-gray-700">Jours ouvrables uniquement</span>
              </label>
              <label className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
                <input
                  data-testid="sla-is-active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(event) => setFormData((current) => ({ ...current, is_active: event.target.checked }))}
                />
                <span className="text-sm text-gray-700">Regle active des maintenant</span>
              </label>
            </div>

            <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-4 text-sm text-gray-700">
              Delai derive: <strong>{Number(formData.min_hours || 0) <= 0 ? `Sous ${Number(formData.max_hours || 0)}h` : `${formData.min_hours || 0}-${formData.max_hours || 0}h`}</strong>
              {formData.business_days_only ? ' ouvrables' : ''}. Le backend garde toujours le dernier mot sur la validation.
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="submit-sla-rule" className="bg-cyan-500 hover:bg-cyan-600" disabled={saving}>
                {saving ? 'Enregistrement...' : editingRule ? 'Mettre a jour' : 'Creer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliverySlaManagement;
